/**
 * QuestionView → UISpec — the TypeScript port of connectors/core/uispec.py's
 * build_ui_spec (parity via golden fixtures generated from the Python side).
 */
import type { QuestionView, UIButton, UISpec } from "./types";

export const GATE_IDS = new Set(["consent", "definition_confirmation"]);
export const MAX_OPTION_BUTTONS = 5;
export const MAX_SCALE_BUTTONS = 10;
export const CONTACT_DECLINE_LABEL = "Prefer not to share";
export const CONTACT_DECLINE_VALUE = "skip";

function emptySpec(): UISpec {
  return {
    kind: "none",
    question_id: "",
    qtype: "",
    buttons: [],
    select: null,
    images: [],
    options: [],
    groups: [],
    min_select: 0,
    max_select: 0,
    allow_pnta: false,
    progress: "",
    hint: "",
  };
}

function progress(view: QuestionView): string {
  if (view.index < 0 || view.count <= 0) return "";
  return `Question ${view.index + 1} of ${view.count}`;
}

function scaleButtons(scale: NonNullable<QuestionView["scale"]>): UIButton[] {
  const lo = Math.trunc(scale.min);
  const hi = Math.trunc(scale.max);
  const buttons: UIButton[] = [];
  for (let n = lo; n <= hi; n++) {
    let label = String(n);
    if (n === lo && scale.min_label) label = `${n} · ${scale.min_label}`;
    else if (n === hi && scale.max_label) label = `${n} · ${scale.max_label}`;
    buttons.push({ label, value: String(n) });
  }
  return buttons;
}

export function buildUISpec(view: QuestionView | null): UISpec {
  const spec = emptySpec();
  if (!view) return spec;
  spec.question_id = view.id;
  spec.qtype = view.qtype;
  spec.progress = progress(view);

  if (view.qtype === "scale" && view.scale) {
    const lo = Math.trunc(view.scale.min);
    const hi = Math.trunc(view.scale.max);
    spec.allow_pnta = true;
    if (hi - lo + 1 <= MAX_SCALE_BUTTONS) {
      spec.kind = "buttons";
      spec.buttons = scaleButtons(view.scale);
    } else {
      spec.kind = "select";
      const options: string[] = [];
      for (let n = lo; n <= hi; n++) options.push(String(n));
      spec.select = { placeholder: "Pick a number", options, min_values: 1, max_values: 1 };
    }
    return spec;
  }

  if (view.qtype === "options" && view.options?.length) {
    spec.allow_pnta = !GATE_IDS.has(view.id);
    if (view.options.length <= MAX_OPTION_BUTTONS) {
      spec.kind = "buttons";
      spec.buttons = view.options.map((o) => ({ label: o, value: o }));
    } else {
      spec.kind = "select";
      spec.select = {
        placeholder: "Choose one",
        options: [...view.options],
        min_values: 1,
        max_values: 1,
      };
    }
    return spec;
  }

  if (view.qtype === "multi_select" && view.options?.length) {
    spec.kind = "multi_select";
    spec.options = [...view.options];
    spec.min_select = view.min_select ?? 0;
    spec.max_select = view.max_select ?? 0;
    spec.select = {
      placeholder: "Choose all that apply",
      options: [...view.options],
      min_values: view.min_select || 0,
      max_values: view.max_select || view.options.length,
    };
    spec.allow_pnta = true;
    return spec;
  }

  if (view.qtype === "ranking" && view.options?.length) {
    spec.kind = "stepwise_ranking";
    spec.options = [...view.options];
    spec.allow_pnta = true;
    return spec;
  }

  if (view.qtype === "card_sort" && view.options?.length && view.groups?.length) {
    spec.kind = "stepwise_cardsort";
    spec.options = [...view.options];
    spec.groups = [...view.groups];
    spec.allow_pnta = true;
    return spec;
  }

  if (view.qtype === "present_images" && view.images?.length) {
    spec.kind = "images";
    spec.images = view.images.map((i) => ({
      url: String(i.url ?? ""),
      label: String(i.label ?? ""),
    }));
    return spec;
  }

  if (view.qtype === "image_upload") {
    spec.kind = "upload";
    spec.hint = view.expected_image ?? "";
    return spec;
  }

  if (view.qtype === "contact") {
    // The details are typed (the widget draws its own form for them); an
    // optional step also gets a decline button for channels without one.
    if (!view.required) {
      spec.kind = "buttons";
      spec.buttons = [{ label: CONTACT_DECLINE_LABEL, value: CONTACT_DECLINE_VALUE }];
    }
    return spec;
  }

  return spec; // open_ended, slots, unknown: plain text
}
